import { createTheme } from '@mui/material/styles';

import { fontFaces } from './fonts';

const spacingTransformation = (factor: number) => 0.4 * factor;

const getTheme = (themeColor?: string) => {
  let theme = createTheme({
    customShadow: '0 0.1rem 1rem rgba(0, 0, 0, 0.24)',
    palette: {
      primary: {
        main: '#3C548E', //primary-600
        pressed: '#5C70A1',
      },
      primaryDark: {
        main: '#212935', //primary-900
      },
      primaryMedium: {
        main: '#B7C3E1', //primary-300
      },
      primaryLight: {
        main: '#F6F8F9', //primary-100
      },
      secondary: {
        main: themeColor || '#F68848', //secondary-600
      },
      grey: {
        '500': '#55575E', //grey-500
        '400': '#707178', //grey-400
        '300': '#91929D', //grey-300
        '200': '#E6E6E6', //grey-200
      },
      error: {
        main: '#AE0016', //error-600
        light: '#F8C6B9', //error-100
      },
      success: {
        main: '#2A7527', //success-600
        light: '#E7F4E6', //success-100
      },
      warning: {
        main: '#CC560A', //warning-600
        light: '#FFDBA1', //warning-100
      },
      info: {
        main: '#3C548E', //info-600
        light: '#DEE7FF', //info-100
      },
    },
    spacing: (factor: number) => `${spacingTransformation(factor)}rem`,
    spacingUnitless: (factor: number) => spacingTransformation(factor),
    shape: {
      borderRadius: 0.4,
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      htmlFontSize: 10,
      h1: {
        fontSize: '9.3rem',
        fontWeight: 300,
        letterSpacing: '-0.15rem',
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      h2: {
        fontSize: '5.8rem',
        fontWeight: 300,
        letterSpacing: '-0.05rem',
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      h3: {
        fontSize: '4.6rem',
        fontWeight: 400,
        letterSpacing: 0,
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      h4: {
        fontSize: '3.3rem',
        fontWeight: 400,
        letterSpacing: '0.025rem',
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      h5: {
        fontSize: '2.3rem',
        fontWeight: 600,
        letterSpacing: 0,
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      h6: {
        fontSize: '1.9rem',
        fontWeight: 600,
        letterSpacing: '0.015rem',
        lineHeight: 1.4,
        textTransform: 'capitalize',
      },
      subtitle1: {
        fontSize: '1.6rem',
        fontWeight: 400,
        letterSpacing: '0.015rem',
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '1.4rem',
        fontWeight: 600,
        letterSpacing: '0.01rem',
        lineHeight: 1.71,
      },
      body1: {
        fontSize: '1.6rem',
        fontWeight: 400,
        letterSpacing: '0.05rem',
        lineHeight: 1.5,
      },
      body1Heading: {
        fontSize: '1.6rem',
        fontWeight: 600,
        letterSpacing: '0.05rem',
        lineHeight: 1.5,
        textTransform: 'capitalize',
      },
      body2: {
        fontSize: '1.4rem',
        fontWeight: 400,
        letterSpacing: '0.015rem',
        lineHeight: 1.43,
      },
      body2Heading: {
        fontSize: '1.4rem',
        fontWeight: 600,
        letterSpacing: '0.015rem',
        lineHeight: 1.43,
        textTransform: 'capitalize',
      },
      button: {
        fontSize: '1.4rem',
        fontWeight: 600,
        textTransform: 'capitalize',
        letterSpacing: 0,
        lineHeight: 1.14,
      },
      link: {
        fontSize: '1.4rem',
        fontWeight: 500,
        letterSpacing: '0.05rem',
        lineHeight: 1.14,
        textTransform: 'capitalize',
        textDecorationLine: 'underline',
      },
      label: {
        fontSize: '1.2rem',
        fontWeight: 500,
        letterSpacing: '0.015rem',
        lineHeight: 1.33,
      },
      caption: {
        fontSize: '1.2rem',
        fontWeight: 400,
        letterSpacing: '0.015rem',
        lineHeight: 1.33,
        textTransform: 'none',
      },
      overline: {
        fontSize: '1rem',
        fontWeight: 400,
        letterSpacing: '0.15rem',
        lineHeight: 1.6,
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontSize: '62.5%',
          },
          body: {
            margin: 0,
            padding: 0,
          },
          fontFaces,
        },
      },
    },
  });

  theme = createTheme(theme, {
    palette: {
      background: {
        image: `linear-gradient(0deg, ${theme.palette.primaryDark.main}CC 0%, ${theme.palette.primaryDark.main}00 53.85%)`,
      },
      text: {
        primary: theme.palette.primaryDark.main,
        secondary: theme.palette.grey[500],
      },
    },
    customSizes: {
      bottomNavigationHeight: 7.3,
      inputHeight: 4.4,
      menuItemHeight: 4.4,
      menuItemSpacing: theme.spacingUnitless(1),
      nativeAuthDialogHeight: 27.4,
      postLoginDownloadPromptHeight: 50,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: theme.palette.primaryLight.main,
            minWidth: 300,
          },
        },
      },
    },
  });

  return theme;
};

export const unreadMessageBadgeColor = '#3C548E';

const defaultTheme = getTheme();

export { getTheme, defaultTheme };
